'use client';

import { getSystemPageUrl } from '@ravago/shared/next-data-access/client';
import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Chip } from '@ravago/shared/radiance/components/chip/components/Chip/Chip';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { useQuery } from '@tanstack/react-query';

import { useProductCompare } from '../../../../hooks/use-product-compare';
import { getLanguageCode } from '../../../../utils/locale/locale.utils';

const MAX_PRODUCTS = 5;

interface Props {
   locale: string;
}

export const ProductCompareSelection = ({ locale }: Props) => {
   const translate = useTranslations();
   const { productItems, removeProduct, isInitialized } = useProductCompare();
   const language = getLanguageCode(locale);

   const { data: productCompareRoute } = useQuery({
      queryKey: ['system-page-route', SystemPageType.PRODUCT_COMPARE],
      queryFn: () => getSystemPageUrl(language, SystemPageType.PRODUCT_COMPARE),
      select: (route) => `/${locale}/${route}`
   });

   const handleRemoveProduct = (productId: string) => {
      removeProduct(productId);
   };

   const onCompareBtnClick = () => {
      if (productCompareRoute) {
         window.open(productCompareRoute, '_blank');
      }
   };

   if (!isInitialized || productItems.length === 0) {
      return null;
   }

   return (
      <Box
         width="100%"
         borderRadius="largest"
         spacing="md"
         items="center"
         background="var(--component-data-display-avatar-container-color-background-account)"
      >
         <Grid>
            <GridCol spanDesktop={10} spanTablet={12} spanPhone={12}>
               <BreakpointSwitch includeViews={['desktop']}>
                  <Box gap="xs" wrap="wrap" items="center">
                     {productItems.map((item) => (
                        <Chip key={item.id} label={item.name} onDelete={() => handleRemoveProduct(item.id)} />
                     ))}
                     {productItems.length === 1 && (
                        <Box justify="end">
                           <Typography variant="small">{translate('page-consumer.product-compare.select-at-least-one-more-product')}</Typography>
                        </Box>
                     )}
                  </Box>
               </BreakpointSwitch>
               <BreakpointSwitch excludeViews={['desktop']}>
                  <Box direction="row" gap="md" items="center" justify="between">
                     <Typography variant="body-highlight">
                        {translate('page-consumer.product-compare.selected-compare-items', { count: productItems.length })}
                     </Typography>
                     {productItems.length === 1 && (
                        <Typography variant="small">{translate('page-consumer.product-compare.select-at-least-one-more-product')}</Typography>
                     )}
                     <Button id="product-compare-button" variant="primary" onClick={onCompareBtnClick} disabled={productItems.length < 2}>
                        <Box direction="row" gap="xs" items="center">
                           <span>{translate('page-consumer.product-compare.compare-button')}</span>
                           <Badge variant={productItems.length >= 2 ? 'inverse-primary' : 'disabled'}>
                              {productItems.length}/{MAX_PRODUCTS}
                           </Badge>
                        </Box>
                     </Button>
                  </Box>
               </BreakpointSwitch>
            </GridCol>
            <BreakpointSwitch includeViews={['desktop']}>
               <GridCol spanDesktop={2} spanTablet={2}>
                  <Box items="center" justify="end" height="100%">
                     <Divider color="default" orientation="vertical" />
                     <Button id="product-compare-button" variant="primary" onClick={onCompareBtnClick} disabled={productItems.length < 2}>
                        <Box direction="row" gap="xs" items="center">
                           <span>{translate('page-consumer.product-compare.compare-button')}</span>
                           <Badge variant={productItems.length >= 2 ? 'inverse-primary' : 'disabled'}>
                              {productItems.length}/{MAX_PRODUCTS}
                           </Badge>
                        </Box>
                     </Button>
                  </Box>
               </GridCol>
            </BreakpointSwitch>
         </Grid>
      </Box>
   );
};
